<template>
  <div v-if="files.length > 0" class="help-file-list">
    <div class="help-file-list__grid">
      <div
        v-for="(file, index) in files"
        :key="index"
        class="help-file-list__item"
        :class="{ 'help-file-list__item--loading': isFileDownloading(file) }"
        :aria-busy="isFileDownloading(file)"
        @click="handleFileClick(file)"
      >
        <div class="help-file-list__icon-wrapper">
          <BaseImage
            :src="getFileIconPath(file)"
            :alt="getFileDisplayName(file)"
            class="help-file-list__icon"
          />
        </div>
        <div class="help-file-list__info">
          <a-tooltip
            :content="getFileDisplayName(file)"
            position="top"
            :mouse-enter-delay="200"
            class="help-file-list__name-wrap"
          >
            <div class="help-file-list__name">{{ getFileDisplayName(file) }}</div>
          </a-tooltip>
          <div class="help-file-list__meta">
            <span v-if="file.fileSize || file.size" class="help-file-list__size">
              {{ formatFileSize(file.fileSize || file.size) }}
            </span>
          </div>
        </div>
        <div class="help-file-list__actions help-file-list__actions--desktop" @click.stop>
          <button
            v-if="canDownloadFile(file)"
            class="help-file-list__action"
            :class="{ 'help-file-list__action--loading': isFileDownloading(file) }"
            type="button"
            title="下载"
            :disabled="isFileDownloading(file)"
            :aria-label="`下载：${getFileDisplayName(file)}`"
            @click="handleFileDownload(file)"
          >
            <icon-loading v-if="isFileDownloading(file)" class="help-file-list__loading-icon" />
            <icon-download v-else />
          </button>
          <SharePopup
            class="help-file-list__share"
            :share-url="getFileShareUrl(file)"
            :share-title="getFileDisplayName(file)"
            @share="handleFileShare(file)"
          >
            <template #trigger>
              <button
                class="help-file-list__action"
                type="button"
                :aria-label="`分享文件：${getFileDisplayName(file)}`"
              >
                <icon-share-internal />
              </button>
            </template>
          </SharePopup>
        </div>
        <div class="help-file-list__mobile-actions" @click.stop>
          <button
            class="help-file-list__more"
            type="button"
            :aria-label="`更多操作：${getFileDisplayName(file)}`"
            @click="openMobileActionSheet(file)"
          >
            <icon-more-vertical />
          </button>
        </div>
      </div>
    </div>
    <BaseActionSheet
      v-model:visible="mobileActionSheetVisible"
      :actions="mobileActionSheetActions"
      :show-close="false"
      max-height="70vh"
      @select="handleMobileActionSelect"
    />
  </div>
</template>

<script setup>
/**
 * @description 文件下载列表组件
 * @param {Array<Object>} files - 文件列表
 * @emits download - 文件下载事件，传递文件对象
 */

import { computed, ref } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import {
  IconDownload,
  IconLoading,
  IconMoreVertical,
  IconShareInternal,
} from '@arco-design/web-vue/es/icon'
import { getAbsoluteUrl, getOssUrl } from '@/utils'
import { openResolvedPreview } from '@/utils/contentPreviewAction'
import { isOssSignedUrl, isOssSignedUrlExpiring } from '@/utils/ossSignedUrl'
import {
  canDownloadFile,
  formatFileSize,
  getFileIconPath,
  getFileOpenUrl,
  resolveDownloadDisplayName,
  resolveDownloadFileName,
} from '@/utils/download'
import BaseActionSheet from '~/components/base/ActionSheet.vue'
import SharePopup from '~/components/base/SharePopup.vue'

const props = defineProps({
  files: {
    type: Array,
    default: () => [],
  },
  downloadingMap: {
    type: Object,
    default: () => ({}),
  },
  resolvePreviewFile: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['download', 'preview', 'share'])
const { withBase, openExternal } = useBasePath()
const mobileActionSheetVisible = ref(false)
const mobileActionFile = ref(null)
const mobileActionSheetActions = computed(() => {
  const actions = []
  if (mobileActionFile.value && canDownloadFile(mobileActionFile.value)) {
    actions.push({
      label: '下载',
      value: 'download',
      disabled: isFileDownloading(mobileActionFile.value),
    })
  }
  actions.push({
    label: '转发',
    value: 'share',
  })
  return actions
})

/**
 * @description 获取文件展示名称，优先使用业务标题
 * @param {Object} file - 文件对象
 * @returns {string}
 */
function getFileDisplayName(file) {
  return resolveDownloadDisplayName(file)
}

/**
 * @description 生成附件下载记录的稳定键值，用于按钮级 loading 状态管理
 * @param {Object} file - 文件对象
 * @returns {string}
 */
function getFileDownloadKey(file) {
  return String(
    file?.contentId ||
      file?.fileUrl ||
      file?.url ||
      file?.downloadUrl ||
      file?.fileName ||
      file?.name ||
      ''
  )
}

/**
 * @description 判断当前文件是否处于下载中
 * @param {Object} file - 文件对象
 * @returns {boolean}
 */
function isFileDownloading(file) {
  return Boolean(props.downloadingMap[getFileDownloadKey(file)])
}

const getFileTargetUrl = (file) => {
  const rawUrl = file?.downloadUrl || file?.fileUrl || file?.url || ''
  return rawUrl ? getOssUrl(rawUrl) : ''
}

const getFilePreviewTargetUrl = (file) => {
  const targetUrl = getFileTargetUrl(file)
  if (file?.contentId && isOssSignedUrl(targetUrl)) {
    return withBase(`/website/service/help/${encodeURIComponent(String(file.contentId))}`)
  }
  const coverUrl = file?.coverUrl || file?.videoCover?.url || ''
  return getFileOpenUrl({
    url: targetUrl,
    fileName: resolveDownloadFileName(file),
    fileType: file?.fileType || file?.type,
    poster: coverUrl ? getOssUrl(coverUrl) : '',
  })
}

const shouldRefreshFilePreview = (file) => {
  const targetUrl = getFileTargetUrl(file)
  const coverUrl = file?.coverUrl || file?.videoCover?.previewUrl || file?.videoCover?.url || ''
  return isOssSignedUrlExpiring(targetUrl) || isOssSignedUrlExpiring(coverUrl)
}

/**
 * @description 处理文件点击
 * @param {Object} file - 文件对象
 */
const handleFileClick = async (file) => {
  if (isFileDownloading(file)) return

  try {
    const result = await openResolvedPreview({
      source: file,
      shouldRefresh: shouldRefreshFilePreview(file),
      refreshSource: props.resolvePreviewFile,
      resolvePreviewUrl: getFilePreviewTargetUrl,
      openUrl: openExternal,
    })
    const actionFile = result.source
    if (result.opened) {
      emit('preview', actionFile)
      return
    }
    if (canDownloadFile(actionFile)) emit('download', actionFile)
  } catch (error) {
    BaseMessage.error('预览资源刷新失败，请稍后重试')
  }
}

const handleFileDownload = (file) => {
  if (!canDownloadFile(file)) return
  if (isFileDownloading(file)) return
  emit('download', file)
}

const getFileShareUrl = (file) => {
  if (!import.meta.client) return ''
  const targetUrl = getFileTargetUrl(file)
  const stableDetailUrl =
    file?.contentId && isOssSignedUrl(targetUrl)
      ? withBase(`/website/service/help/${encodeURIComponent(String(file.contentId))}`)
      : ''
  return getAbsoluteUrl(stableDetailUrl || getFilePreviewTargetUrl(file) || targetUrl)
}

const getFileShareCopyText = (file) => {
  const shareUrl = getFileShareUrl(file)
  const title = getFileDisplayName(file).trim()
  return title ? `${title}，文件详情链接：${shareUrl}` : shareUrl
}

const handleFileShare = (file) => {
  emit('share', file)
}

const openMobileActionSheet = (file) => {
  mobileActionFile.value = file
  mobileActionSheetVisible.value = true
}

const handleMobileActionSelect = ({ action }) => {
  if (!mobileActionFile.value) return
  if (action?.value === 'download') {
    handleFileDownload(mobileActionFile.value)
    return
  }
  if (action?.value === 'share') {
    handleMobileShare(mobileActionFile.value)
  }
}

const handleMobileShare = async (file) => {
  if (!import.meta.client) return

  const shareUrl = getFileShareUrl(file)
  try {
    if (navigator.share) {
      await navigator.share({
        title: getFileDisplayName(file),
        url: shareUrl,
      })
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(getFileShareCopyText(file))
      BaseMessage.success('已复制链接')
    } else {
      BaseMessage.info('请复制页面链接进行分享')
    }
    handleFileShare(file)
  } catch (error) {
    if (error?.name !== 'AbortError') {
      BaseMessage.error('分享失败，请稍后重试')
    }
  }
}
</script>

<style lang="scss" scoped>
.help-file-list {
  margin-top: 0;
  margin-bottom: 0;

  @include respond-to-max('xl') {
    margin-top: 0;
  }

  @include respond-to-max('lg') {
    margin-top: 16px;
  }
}

.help-file-list__title {
  font-size: 24px;
  font-weight: 600;
  color: $text-title-color;
  margin: 0 0 24px 0;

  @include respond-to-max('lg') {
    font-size: 20px;
    margin-bottom: 20px;
  }
}

.help-file-list__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;

  @include respond-to-max('lg') {
    grid-template-columns: 1fr;
    gap: 12px;
    min-width: 0;
  }
}

.help-file-list__item {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 16px;
  background-color: $bg-white;
  border: 1px solid $border-light-color;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  box-sizing: border-box;

  &:hover {
    border-color: $border-light-color;
    background-color: #f7f8fa;
  }

  &--loading {
    border-color: $border-light-color;
    background-color: #f7f8fa;
    cursor: not-allowed;
  }

  @include respond-to-max('lg') {
    max-width: none;
    width: 100%;
    min-width: 0;
    gap: 8px;
    padding: 12px;
  }
}

.help-file-list__icon-wrapper {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $bg-white;
  border-radius: 8px;

  @include respond-to-max('lg') {
    width: 36px;
    height: 36px;
  }
}

.help-file-list__icon {
  width: 44px;
  height: 44px;
  object-fit: contain;
  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @include respond-to-max('lg') {
    width: 36px;
    height: 36px;
  }
}

.help-file-list__info {
  flex: 1;
  min-width: 0;
}

.help-file-list__name-wrap {
  min-width: 0;
  overflow: hidden;
  display: block;
}

.help-file-list__name {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: $text-title-color;
  letter-spacing: 0;
  margin: 0 0 4px 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include respond-to-max('lg') {
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0;
    margin-bottom: 2px;
  }
}

.help-file-list__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0;
  color: $text-subtitle-color;

  @include respond-to-max('lg') {
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    gap: 8px;
  }
}

.help-file-list__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @include respond-to-max('lg') {
    display: none;
  }
}

.help-file-list__mobile-actions {
  display: none;
  flex: 0 0 auto;

  @include respond-to-max('lg') {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.help-file-list__action {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background-color: transparent;
  color: $text-content-color;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    color 0.3s,
    background-color 0.3s;

  &:hover {
    color: $text-title-color;
    background-color: transparent;
  }

  &:disabled {
    cursor: not-allowed;
    color: $primary-color;
  }

  :deep(.arco-icon),
  .arco-icon {
    font-size: 20px;
  }

  @include respond-to-max('lg') {
    width: 32px;
    height: 32px;
  }
}

.help-file-list__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $text-content-color;
  cursor: pointer;

  :deep(.arco-icon),
  .arco-icon {
    font-size: 18px;
  }
  @include respond-to-max('lg') {
    :deep(.arco-icon),
    .arco-icon {
      font-size: 16px;
    }
  }
}

.help-file-list__loading-icon {
  font-size: 20px;
  color: $primary-color;
  animation: arco-loading-circle 1s linear infinite;
}
</style>
